"use client";

import Image from "next/image";
import ScrollAnimation from "./ScrollAnimation";
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";

const members = [
  { name: "Anna Bach", role: "Bass, Gesang", img: "/img/profil/anna.jpg" },
  { name: "Nils Simmert", role: "Gitarre, Bouzouki, Tin Whistle", img: "/img/profil/nils.jpg" },
  { name: "Till Rammelt", role: "Schlagzeug", img: "/img/profil/till.jpg" },
  { name: "Fabio Geyer", role: "Gitarre, Gesang", img: "/img/profil/fabio.jpg" },
];

function MemberCard({ member }: { member: typeof members[0] }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <div 
      className="relative group border border-white/10 bg-black/40 backdrop-blur-sm rounded-2xl overflow-hidden p-8 shadow-xl transition-all duration-500 w-[350px] flex flex-col items-center"
      onMouseMove={handleMouseMove}
    >
      {/* Spotlight Effect Layer */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              400px circle at ${mouseX}px ${mouseY}px,
              rgba(222, 204, 164, 0.15),
              transparent 80%
            )
          `,
        }}
      />

      <div className="relative w-[220px] h-[320px] mx-auto mb-6 rounded-2xl overflow-hidden border-4 border-secondary shadow-lg group-hover:border-accent transition-colors duration-500 z-10">
        <Image 
          src={member.img} 
          alt={member.name} 
          fill 
          className="object-cover transition-transform duration-500 group-hover:scale-110"
        />
      </div>
      
      <h3 className="text-2xl text-accent mb-2 font-oswald uppercase tracking-wider z-10">{member.name}</h3>
      <p className="font-serif italic opacity-80 text-gray-300 z-10">{member.role}</p>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
  );
}

export default function BandSection() {
  return (
    <section className="section-block band-members text-center" id="band">
      <ScrollAnimation animation="fade-in">
        <h2>Band</h2>
      </ScrollAnimation>
      
      <ScrollAnimation animation="slide-up" delay={200}>
        <div className="group-photo-container flex justify-center mb-16 px-10">
          <Image 
            src="/img/Gruppenbild.jpeg" 
            alt="Becoming Wind Gruppenbild" 
            width={1200} 
            height={800} 
            className="rounded-2xl border-4 border-secondary shadow-2xl hover:scale-[1.02] hover:border-accent transition-all duration-500 max-w-4xl w-full"
          />
        </div>
      </ScrollAnimation>

      <div className="member-grid flex flex-wrap justify-center gap-10 mt-12">
        {members.map((member, i) => (
          <ScrollAnimation key={i} animation="scale-up" delay={i * 100}>
            <MemberCard member={member} />
          </ScrollAnimation>
        ))}
      </div>
    </section>
  );
}
